import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';

const LIST_MANAGEMENT_DIALOG_COPY = {
  de: {
    errors: {
      enterName: 'Bitte geben Sie einen Listennamen ein',
    },
    messages: {
      created: 'Liste "{{name}}" wurde erstellt',
      renamed: 'Liste wurde in "{{name}}" umbenannt',
      deleted: 'Liste "{{name}}" wurde gelöscht',
      duplicated: 'Liste "{{name}}" wurde erstellt',
      alreadyActive: 'Diese Liste ist bereits aktiv',
      maxActive: 'Maximal 2 Arbeitslisten können gleichzeitig aktiv sein',
      activated: '"{{name}}" wurde als Arbeitsliste aktiviert',
    },
    title: 'Listen verwalten',
    newList: 'Neue Liste',
    newListTooltip: 'Neue wiederverwendbare Parameterliste erstellen',
    searchPlaceholder: 'Listen durchsuchen...',
    privateLists: 'Meine privaten Listen',
    publicLists: 'Öffentliche Listen',
    active: 'Aktiv',
    standard: 'Standard',
    suggestions: 'Vorschläge',
    parameters: '{{count}} Parameter',
    noListsFound: 'Keine Listen gefunden',
    noPrivateLists: 'Noch keine privaten Listen vorhanden',
    noPublicLists: 'Keine öffentlichen Listen verfügbar',
    close: 'Schließen',
    menu: {
      activate: 'Als Arbeitsliste aktivieren',
      duplicate: 'Duplizieren',
      rename: 'Umbenennen',
      delete: 'Löschen',
    },
    createDialog: {
      title: 'Neue Liste erstellen',
      nameLabel: 'Listenname',
      private: 'Privat',
      public: 'Öffentlich',
      listType: 'Nutzung',
      listTypeTooltip: 'Listen sind jetzt generisch und können sowohl für Kohortenarbeit als auch für Extraktionen wiederverwendet werden.',
      generic: 'Wiederverwendbare Liste',
      genericTooltip: 'Diese Liste kann später sowohl für Kohorten- als auch Extraktionsschritte ausgewählt werden',
      cancel: 'Abbrechen',
      confirm: 'Erstellen',
    },
    renameDialog: {
      title: 'Liste umbenennen',
      label: 'Neuer Name',
      cancel: 'Abbrechen',
      confirm: 'Umbenennen',
    },
    deleteDialog: {
      title: 'Liste löschen?',
      confirmPrompt: 'Möchten Sie die Liste "{{name}}" wirklich löschen?',
      warning: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirm: 'Löschen',
    },
    listTypes: {
      generic: 'Wiederverwendbare Liste',
      criteria: 'List A (Kohortenparameter)',
      extraction: 'List B (Extraktion)',
      copySuffix: '(Kopie)',
    },
  },
  en: {
    errors: {
      enterName: 'Please enter a list name',
    },
    messages: {
      created: 'List "{{name}}" was created',
      renamed: 'List was renamed to "{{name}}"',
      deleted: 'List "{{name}}" was deleted',
      duplicated: 'List "{{name}}" was created',
      alreadyActive: 'This list is already active',
      maxActive: 'A maximum of 2 working lists can be active at the same time',
      activated: '"{{name}}" was activated as a working list',
    },
    title: 'Manage lists',
    newList: 'New list',
    newListTooltip: 'Create a new reusable parameter list',
    searchPlaceholder: 'Search lists...',
    privateLists: 'My private lists',
    publicLists: 'Public lists',
    active: 'Active',
    standard: 'Default',
    suggestions: 'Suggestions',
    parameters: '{{count}} parameters',
    noListsFound: 'No lists found',
    noPrivateLists: 'No private lists available yet',
    noPublicLists: 'No public lists available',
    close: 'Close',
    menu: {
      activate: 'Activate as working list',
      duplicate: 'Duplicate',
      rename: 'Rename',
      delete: 'Delete',
    },
    createDialog: {
      title: 'Create new list',
      nameLabel: 'List name',
      private: 'Private',
      public: 'Public',
      listType: 'Usage',
      listTypeTooltip: 'Lists are now generic and can be reused later for both cohort definition and extraction.',
      generic: 'Reusable list',
      genericTooltip: 'This list can later be selected in both cohort and extraction workflows',
      cancel: 'Cancel',
      confirm: 'Create',
    },
    renameDialog: {
      title: 'Rename list',
      label: 'New name',
      cancel: 'Cancel',
      confirm: 'Rename',
    },
    deleteDialog: {
      title: 'Delete list?',
      confirmPrompt: 'Do you really want to delete the list "{{name}}"?',
      warning: 'This action cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    listTypes: {
      generic: 'Reusable list',
      criteria: 'List A (Cohort parameters)',
      extraction: 'List B (Extraction)',
      copySuffix: '(Copy)',
    },
  },
  pt: {
    errors: {
      enterName: 'Por favor, insira um nome para a lista',
    },
    messages: {
      created: 'A lista "{{name}}" foi criada',
      renamed: 'A lista foi renomeada para "{{name}}"',
      deleted: 'A lista "{{name}}" foi excluída',
      duplicated: 'A lista "{{name}}" foi criada',
      alreadyActive: 'Esta lista já está ativa',
      maxActive: 'No máximo 2 listas de trabalho podem estar ativas ao mesmo tempo',
      activated: '"{{name}}" foi ativada como lista de trabalho',
    },
    title: 'Gerenciar listas',
    newList: 'Nova lista',
    newListTooltip: 'Criar uma nova lista de parâmetros reutilizável',
    searchPlaceholder: 'Pesquisar listas...',
    privateLists: 'Minhas listas privadas',
    publicLists: 'Listas públicas',
    active: 'Ativa',
    standard: 'Padrão',
    suggestions: 'Sugestões',
    parameters: '{{count}} parâmetros',
    noListsFound: 'Nenhuma lista encontrada',
    noPrivateLists: 'Ainda não há listas privadas disponíveis',
    noPublicLists: 'Nenhuma lista pública disponível',
    close: 'Fechar',
    menu: {
      activate: 'Ativar como lista de trabalho',
      duplicate: 'Duplicar',
      rename: 'Renomear',
      delete: 'Excluir',
    },
    createDialog: {
      title: 'Criar nova lista',
      nameLabel: 'Nome da lista',
      private: 'Privada',
      public: 'Pública',
      listType: 'Uso',
      listTypeTooltip: 'As listas agora são genéricas e podem ser reutilizadas depois tanto para coorte quanto para extração.',
      generic: 'Lista reutilizável',
      genericTooltip: 'Esta lista poderá ser selecionada depois nos fluxos de coorte e extração',
      cancel: 'Cancelar',
      confirm: 'Criar',
    },
    renameDialog: {
      title: 'Renomear lista',
      label: 'Novo nome',
      cancel: 'Cancelar',
      confirm: 'Renomear',
    },
    deleteDialog: {
      title: 'Excluir lista?',
      confirmPrompt: 'Deseja realmente excluir a lista "{{name}}"?',
      warning: 'Esta ação não pode ser desfeita.',
      cancel: 'Cancelar',
      confirm: 'Excluir',
    },
    listTypes: {
      generic: 'Lista reutilizável',
      criteria: 'List A (Parâmetros de coorte)',
      extraction: 'List B (Extração)',
      copySuffix: '(Cópia)',
    },
  },
};

/**
 * List Management Dialog Component
 * Comprehensive dialog for managing working lists
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Array} parameterLists - All parameter lists
 * @param {Function} setParameterLists - Setter for parameter lists
 * @param {string} currentUser - Current user name
 * @param {Array} activeWorkingList - Currently active working list IDs
 * @param {Function} setActiveWorkingList - Setter for active working list
 * @param {Function} setSnackbar - Function to show snackbar feedback
 */
const ListManagementDialog = ({
  open,
  onClose,
  parameterLists,
  activeWorkingList,
  setActiveWorkingList,
  setSnackbar,
  onCreateList,
  onRenameList,
  onDeleteList,
  onDuplicateList,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = LIST_MANAGEMENT_DIALOG_COPY[language];

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Create list dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListPrivate, setNewListPrivate] = useState(true);
  const [newListType, setNewListType] = useState('generic');

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingList, setRenamingList] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingList, setDeletingList] = useState(null);

  // Menu state (for list actions)
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedList, setSelectedList] = useState(null);

  // Filter lists by search
  const filteredLists = parameterLists?.filter(list =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Separate private and public lists
  const myPrivateLists = filteredLists.filter(l => l.isPrivate);
  const publicLists = filteredLists.filter(l => !l.isPrivate);

  // Handlers
  const handleOpenMenu = (event, list) => {
    setMenuAnchor(event.currentTarget);
    setSelectedList(list);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedList(null);
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) {
      setSnackbar({
        open: true,
        message: copy.errors.enterName,
        severity: 'error'
      });
      return;
    }

    try {
      const newList = await onCreateList({
        name: newListName.trim(),
        listType: newListType,
        isPrivate: newListPrivate,
      });

      setCreateDialogOpen(false);
      setNewListName('');
      setNewListPrivate(true);
      setNewListType('generic');

      setSnackbar({
        open: true,
        message: copy.messages.created.replace('{{name}}', newList.name),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || copy.errors.enterName,
        severity: 'error'
      });
    }
  };

  const handleRenameList = async () => {
    if (!renameValue.trim()) {
      setSnackbar({
        open: true,
        message: copy.errors.enterName,
        severity: 'error'
      });
      return;
    }

    try {
      await onRenameList(renamingList.id, renameValue.trim());

      setSnackbar({
        open: true,
        message: copy.messages.renamed.replace('{{name}}', renameValue.trim()),
        severity: 'success'
      });

      setRenameDialogOpen(false);
      setRenamingList(null);
      setRenameValue('');
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || copy.errors.enterName,
        severity: 'error'
      });
    }
  };

  const handleDeleteList = async () => {
    try {
      await onDeleteList(deletingList.id);

      if (activeWorkingList.includes(deletingList.id)) {
        setActiveWorkingList(prev => prev.filter(id => id !== deletingList.id));
      }

      setSnackbar({
        open: true,
        message: copy.messages.deleted.replace('{{name}}', deletingList.name),
        severity: 'success'
      });

      setDeleteDialogOpen(false);
      setDeletingList(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || copy.deleteDialog.warning,
        severity: 'error'
      });
    }
  };

  const handleDuplicateList = async (list) => {
    try {
      const duplicatedList = await onDuplicateList(list.id);

      setSnackbar({
        open: true,
        message: copy.messages.duplicated.replace('{{name}}', duplicatedList.name),
        severity: 'success'
      });

      handleCloseMenu();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || copy.messages.duplicated.replace('{{name}}', list.name),
        severity: 'error'
      });
    }
  };

  const handleSetAsActiveList = (listId) => {
    if (activeWorkingList.includes(listId)) {
      setSnackbar({
        open: true,
        message: copy.messages.alreadyActive,
        severity: 'info'
      });
    } else if (activeWorkingList.length >= 2) {
      setSnackbar({
        open: true,
        message: copy.messages.maxActive,
        severity: 'warning'
      });
    } else {
      setActiveWorkingList(prev => [...prev, listId]);
      const list = parameterLists.find(l => l.id === listId);
      setSnackbar({
        open: true,
        message: copy.messages.activated.replace('{{name}}', list.name),
        severity: 'success'
      });
    }
    handleCloseMenu();
  };

  const handleOpenRename = (list) => {
    setRenamingList(list);
    setRenameValue(list.name);
    setRenameDialogOpen(true);
    handleCloseMenu();
  };

  const handleOpenDelete = (list) => {
    setDeletingList(list);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const getListTypeLabel = (listType) =>
    listType === 'extraction'
      ? copy.listTypes.extraction
      : listType === 'criteria'
        ? copy.listTypes.criteria
        : copy.listTypes.generic;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaylistAddCheckIcon />
              <Typography variant="h6" fontWeight="bold">
                {copy.title}
              </Typography>
            </Box>
            <Tooltip title={copy.newListTooltip} arrow>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                {copy.newList}
              </Button>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder={copy.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
            sx={{ mb: 3 }}
          />

          {/* Private Lists */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LockIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                {copy.privateLists}
              </Typography>
              <Chip label={myPrivateLists.length} size="small" />
            </Box>

            {myPrivateLists.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {myPrivateLists.map(list => (
                  <Card key={list.id} variant="outlined">
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {list.name}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={getListTypeLabel(list.listType)}
                            />
                            {activeWorkingList.includes(list.id) && (
                              <Chip
                                label={copy.active}
                                size="small"
                                color="primary"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                            {list.isDefault && (
                              <Chip label={copy.standard} size="small" color="secondary" />
                            )}
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {copy.parameters.replace('{{count}}', list.parameterIds.length)}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => handleOpenMenu(e, list)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                {searchTerm ? copy.noListsFound : copy.noPrivateLists}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Public Lists */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PublicIcon fontSize="small" color="action" />
              <Typography variant="subtitle1" fontWeight="bold">
                {copy.publicLists}
              </Typography>
              <Chip label={publicLists.length} size="small" />
            </Box>

            {publicLists.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {publicLists.map(list => {
                  const hasPendingRequests = list.additionRequests && list.additionRequests.length > 0;
                  return (
                    <Card key={list.id} variant="outlined">
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {list.name}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={getListTypeLabel(list.listType)}
                              />
                              {activeWorkingList.includes(list.id) && (
                                <Chip
                                  label={copy.active}
                                  size="small"
                                  color="primary"
                                  icon={<CheckCircleIcon />}
                                />
                              )}
                              {hasPendingRequests && (
                                <Badge badgeContent={list.additionRequests.length} color="warning">
                                  <Chip
                                    label={copy.suggestions}
                                    size="small"
                                    color="warning"
                                    icon={<WarningAmberIcon />}
                                  />
                                </Badge>
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {copy.parameters.replace('{{count}}', list.parameterIds.length)} • {list.createdBy}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={(e) => handleOpenMenu(e, list)}>
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                {copy.noPublicLists}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{copy.close}</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {selectedList && !activeWorkingList.includes(selectedList.id) && (
          <MenuItem onClick={() => handleSetAsActiveList(selectedList.id)}>
            <ListItemIcon>
              <PlaylistAddCheckIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{copy.menu.activate}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={() => handleDuplicateList(selectedList)}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{copy.menu.duplicate}</ListItemText>
        </MenuItem>

        {selectedList && selectedList.isPrivate && (
          <>
            <Divider />
            <MenuItem onClick={() => handleOpenRename(selectedList)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{copy.menu.rename}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenDelete(selectedList)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>{copy.menu.delete}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create List Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{copy.createDialog.title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={copy.createDialog.nameLabel}
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={copy.createDialog.private}
              icon={<LockIcon />}
              color={newListPrivate ? "primary" : "default"}
              onClick={() => setNewListPrivate(true)}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label={copy.createDialog.public}
              icon={<PublicIcon />}
              color={!newListPrivate ? "primary" : "default"}
              onClick={() => setNewListPrivate(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {copy.createDialog.listType}
              </Typography>
              <Tooltip title={copy.createDialog.listTypeTooltip} arrow>
                <HelpOutlineIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title={copy.createDialog.genericTooltip} arrow>
                <Chip
                  label={copy.createDialog.generic}
                  color={newListType === 'generic' ? 'primary' : 'default'}
                  onClick={() => setNewListType('generic')}
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewListType('generic');
            }}
          >
            {copy.createDialog.cancel}
          </Button>
          <Button onClick={handleCreateList} variant="contained">{copy.createDialog.confirm}</Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{copy.renameDialog.title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={copy.renameDialog.label}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>{copy.renameDialog.cancel}</Button>
          <Button onClick={handleRenameList} variant="contained">{copy.renameDialog.confirm}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{copy.deleteDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>
            {copy.deleteDialog.confirmPrompt.replace('{{name}}', deletingList?.name || '')}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {copy.deleteDialog.warning}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{copy.deleteDialog.cancel}</Button>
          <Button onClick={handleDeleteList} variant="contained" color="error">{copy.deleteDialog.confirm}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListManagementDialog;
